import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getServiceErrorKey } from '../serviceErrors'
import { useConfirmationResult } from '../useConfirmationResult'

import { useServiceRoleLabels } from './roles'
import { ConfirmationResultModal, Flex, FormField, Select } from '@percent/lemonade'
import type { SelectOption } from '@percent/lemonade'
import { AccountOrGroupPicker } from '@percent/domain/iam'
import type { AssigneeSearchResult } from '@percent/domain/iam'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useSearchAssignees } from '@percent/admin-dashboard/app/accounts/hooks'
import {
  isInternalServiceRole,
  type InternalService,
  type InternalServiceRole
} from '@percent/admin-dashboard/api/actions/internalServices/internalServices.types'

type GrantServiceRoleModalProps = {
  service: InternalService
  /** Holders that already have a role, left out of the search. */
  heldBy: string[]
  onClose: () => void
  onChanged: () => void
}

export function GrantServiceRoleModal({ service, heldBy, onClose, onChanged }: Readonly<GrantServiceRoleModalProps>) {
  const { t } = useTranslation()
  const { internalServiceService } = useServices()
  const searchAll = useSearchAssignees()
  const { options } = useServiceRoleLabels()
  const [holder, setHolder] = useState<AssigneeSearchResult | null>(null)
  const [role, setRole] = useState<InternalServiceRole | null>(null)
  const [{ isPending }, { mutateAsync: grantRole }] = useMutation(internalServiceService.grantServiceRole)
  const result = useConfirmationResult({ onClose, onSuccess: onChanged, isBusy: isPending })

  const searchAssignees = useCallback(
    async (query: string) => (await searchAll(query)).filter(({ id }) => !heldBy.includes(id)),
    [searchAll, heldBy]
  )

  const confirm = async () => {
    if (!holder || !role) return

    const granted = await result.run(() => grantRole({ serviceId: service.id, holderId: holder.id, role }))

    // A refusal means the roles shown were out of date, so they are read again
    if (!granted) onChanged()
  }

  return (
    <ConfirmationResultModal
      isOpen
      state={result.state}
      title={t('modal.grantServiceRole.title', { name: service.name })}
      primaryButtonText={t('button.grantRole')}
      loadingText={t('modal.grantServiceRole.button.granting')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={confirm}
      onClose={result.dismiss}
      onFailureClose={result.backToConfirm}
      disabled={!holder || !role || isPending}
      loading={isPending}
      primaryBtnTestId="grant-service-role-submit-button"
      secondaryBtnTestId="grant-service-role-cancel-button"
      buttonTestId="grant-service-role-result-button"
      success={{
        title: t('modal.grantServiceRole.success.title'),
        description: t('modal.grantServiceRole.success.description', { name: holder?.name, service: service.name }),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.grantServiceRole.error.title'),
        description: t(getServiceErrorKey(result.error) ?? 'modal.grantServiceRole.error.description'),
        button: t('button.close')
      }}
    >
      <Flex direction="column" gap="md">
        <AccountOrGroupPicker
          dataTestId="grant-service-role"
          searchAssignees={searchAssignees}
          value={holder}
          onChange={setHolder}
        />
        <FormField
          label={t('typography.role')}
          description={t('modal.grantServiceRole.role.hint')}
          necessity="required"
        >
          <Select
            name="role"
            placeholder={t('modal.assignRole.rolePlaceholder')}
            // The modal remounts its form after a failure, so the select shows the role still chosen
            defaultValue={options.find(option => option.value === role)}
            options={options}
            onChange={(option: SelectOption) => isInternalServiceRole(option.value) && setRole(option.value)}
            data-testid="grant-service-role-role-select"
          />
        </FormField>
      </Flex>
    </ConfirmationResultModal>
  )
}
